import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from 'shared/components/UC/Button';
import adminService from '../../services/adminService';
import { User } from '../../types/auth';
import EvaluatorNotesModal from './EvaluatorNotesModal';
import styles from './EvaluadoresTab.module.css';

interface ActionItem { label: string; handler: () => void; }

const EvaluadorActionsMenu: React.FC<{ actions: ActionItem[] }> = ({ actions }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => {
      if (buttonRef.current) {
        const r = buttonRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom, left: r.right });
      }
    };
    if (isOpen) {
      update();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
    }
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  return (
    <div className={styles.actionsMenuContainer} ref={buttonRef}>
      <div ref={menuRef}>
        <Button variant="primary" size="sm" onClick={() => setIsOpen((o) => !o)} className={styles.actionButton}>
          Acciones
          <i className="uc-icon icon-size--sm" style={{ marginLeft: '0.25rem' }}>expand_more</i>
        </Button>
        {isOpen && dropdownPos && (
          <div
            className={styles.actionsMenuDropdown}
            style={{ top: `${dropdownPos.top}px`, left: `${dropdownPos.left}px`, transform: 'translateX(-100%)' }}
          >
            {actions.map((a) => (
              <button key={a.label} className={styles.actionsMenuItem} onClick={() => { setIsOpen(false); a.handler(); }}>
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface Props {
  formTemplateId: string | null;
  onToast: (msg: string) => void;
}

// TODO(fullstack): isExternal / isConfirmed come from the identity provider.
// researcherType comes from the researcher registry.
type StoredUser = User & { isExternal: boolean; isConfirmed: boolean };

type ContestStatus = 'asignado' | 'habilitado' | 'excluido';

interface Row {
  user: StoredUser;
  contestStatus: ContestStatus;
}

interface ProfileSummary {
  score: number | null;
  latestNote: string | null;
}

const EvaluadoresTab: React.FC<Props> = ({ formTemplateId, onToast }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addIsExternal, setAddIsExternal] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [profileMap, setProfileMap] = useState<Map<string, ProfileSummary>>(new Map());
  const [notesModal, setNotesModal] = useState<{
    evaluatorId: string;
    evaluatorName: string;
    isExternal: boolean;
  } | null>(null);
  type SortCol = 'nombre' | 'tipo' | 'concurso' | 'score' | 'nota';
  const [sortCol, setSortCol] = useState<SortCol>('concurso');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleSort = (col: SortCol) => {
    setSortDir((d) => (sortCol === col && d === 'asc' ? 'desc' : 'asc'));
    setSortCol(col);
  };

  const sortIconClass = (col: SortCol) =>
    sortCol === col ? styles.sortIconActive : styles.sortIcon;

  const sortIconName = (col: SortCol) =>
    sortCol === col && sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward';

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [usersResp, boardRows, excludedIds] = await Promise.all([
        adminService.getAllUsers({ role: 'evaluator', limit: 200 } as any),
        adminService.getEvaluatorBoardData(formTemplateId),
        adminService.getContestExclusions(formTemplateId ?? ''),
      ]);
      const users = usersResp.users as StoredUser[];
      const assignedIds = new Set(boardRows.map((r) => r.evaluatorId));
      const excludedSet = new Set(excludedIds);
      setRows(
        users.map((u) => {
          let contestStatus: ContestStatus = 'habilitado';
          if (excludedSet.has(u.id)) contestStatus = 'excluido';
          else if (assignedIds.has(u.id)) contestStatus = 'asignado';
          return { user: u, contestStatus };
        })
      );

      const profiles = await Promise.all(users.map((u) => adminService.getEvaluatorProfile(u.id)));
      const map = new Map<string, ProfileSummary>();
      users.forEach((u, i) => {
        const p = profiles[i];
        const noteArr: any[] = p.notes ?? [];
        map.set(u.id, {
          score: p.score ?? null,
          latestNote: noteArr.length ? noteArr[noteArr.length - 1].text : null,
        });
      });
      setProfileMap(map);
    } finally {
      setIsLoading(false);
    }
  }, [formTemplateId]);

  const handleProfileChanged = async (evaluatorId: string) => {
    const p: any = await adminService.getEvaluatorProfile(evaluatorId);
    const noteArr: any[] = p.notes ?? [];
    setProfileMap((prev) => {
      const next = new Map(prev);
      next.set(evaluatorId, {
        score: p.score ?? null,
        latestNote: noteArr.length ? noteArr[noteArr.length - 1].text : null,
      });
      return next;
    });
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleExclude = async (u: StoredUser) => {
    if (!formTemplateId) return;
    await adminService.excludeEvaluatorFromContest(formTemplateId, u.id);
    onToast(`${u.name} excluido de este concurso.`);
    load();
  };

  const handleInclude = async (u: StoredUser) => {
    if (!formTemplateId) return;
    await adminService.includeEvaluatorInContest(formTemplateId, u.id);
    onToast(`${u.name} habilitado para este concurso.`);
    load();
  };

  const handleAdd = async () => {
    if (!addName.trim() || !addEmail.trim()) return;
    setAddSaving(true);
    try {
      await adminService.createUser({
        name: addName.trim(),
        email: addEmail.trim().toLowerCase(),
        role: 'evaluator',
        isActive: true,
        isExternal: addIsExternal,
      });
      onToast(`Evaluador ${addName.trim()} creado correctamente.`);
      setShowAdd(false);
      setAddName('');
      setAddEmail('');
      setAddIsExternal(false);
      load();
    } finally {
      setAddSaving(false);
    }
  };

  const closeAdd = () => {
    setShowAdd(false);
    setAddName('');
    setAddEmail('');
    setAddIsExternal(false);
  };

  const q = search.toLowerCase();
  const filtered = rows.filter(
    ({ user: u }) =>
      !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
  );
  const contestOrder: Record<ContestStatus, number> = { asignado: 0, habilitado: 1, excluido: 2 };
  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const pa = profileMap.get(a.user.id);
    const pb = profileMap.get(b.user.id);
    switch (sortCol) {
      case 'nombre':
        return dir * a.user.name.localeCompare(b.user.name, 'es');
      case 'tipo':
        return dir * ((a.user.isExternal ? 1 : 0) - (b.user.isExternal ? 1 : 0));
      case 'concurso':
        return dir * (contestOrder[a.contestStatus] - contestOrder[b.contestStatus]);
      case 'score': {
        const sa = pa?.score ?? -Infinity;
        const sb = pb?.score ?? -Infinity;
        return dir * (sa - sb);
      }
      case 'nota':
        return dir * ((pa?.latestNote ?? '').localeCompare(pb?.latestNote ?? '', 'es'));
      default:
        return 0;
    }
  });

  const countAsignados = rows.filter((r) => r.contestStatus === 'asignado').length;
  const countHabilitados = rows.filter((r) => r.contestStatus === 'habilitado').length;
  const countExcluidos = rows.filter((r) => r.contestStatus === 'excluido').length;

  return (
    <div className={styles.wrapper}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchContainer}>
          <svg
            className={styles.searchIcon}
            width="15"
            height="15"
            viewBox="0 0 15 15"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10.4167 9.16667H9.75833L9.525 8.94167C10.3417 7.99167 10.8333 6.75833 10.8333 5.41667C10.8333 2.425 8.40833 0 5.41667 0C2.425 0 0 2.425 0 5.41667C0 8.40833 2.425 10.8333 5.41667 10.8333C6.75833 10.8333 7.99167 10.3417 8.94167 9.525L9.16667 9.75833V10.4167L13.3333 14.575L14.575 13.3333L10.4167 9.16667ZM5.41667 9.16667C3.34167 9.16667 1.66667 7.49167 1.66667 5.41667C1.66667 3.34167 3.34167 1.66667 5.41667 1.66667C7.49167 1.66667 9.16667 3.34167 9.16667 5.41667C9.16667 7.49167 7.49167 9.16667 5.41667 9.16667Z"
              fill="currentColor"
            />
          </svg>
          <input
            className={styles.searchInput}
            placeholder="Buscar por nombre o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className={styles.toolbarRight}>
          <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
            Añadir evaluador
          </Button>
        </div>
      </div>

      {/* Summary */}
      {!isLoading && (
        <div className={styles.summary}>
          <span>
            <strong>{countAsignados}</strong> asignados
          </span>
          <span className={styles.summarySep}>·</span>
          <span>
            <strong>{countHabilitados}</strong> habilitados
          </span>
          <span className={styles.summarySep}>·</span>
          <span>
            <strong>{countExcluidos}</strong> excluidos
          </span>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Cargando evaluadores...</span>
        </div>
      ) : sorted.length === 0 ? (
        <div className={styles.empty}>
          {rows.length === 0
            ? 'No hay evaluadores registrados en el sistema.'
            : 'Ningún evaluador coincide con la búsqueda.'}
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                {(['nombre', 'tipo', 'concurso', 'score', 'nota'] as SortCol[]).map((col) => (
                  <th
                    key={col}
                    className={styles.sortableHeader}
                    onClick={() => toggleSort(col)}
                    aria-sort={sortCol === col ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <span className={styles.sortHeaderContent}>
                      {col === 'nombre' && 'Nombre'}
                      {col === 'tipo' && 'Tipo'}
                      {col === 'concurso' && 'Este concurso'}
                      {col === 'score' && 'Score'}
                      {col === 'nota' && 'Nota'}
                      <i className={`uc-icon ${sortIconClass(col)}`} aria-hidden="true">
                        {sortIconName(col)}
                      </i>
                    </span>
                  </th>
                ))}
                <th className={styles.actionsHeader}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(({ user: u, contestStatus }) => {
                const profile = profileMap.get(u.id);
                const actions: ActionItem[] = [
                  {
                    label: 'Notas / score',
                    handler: () => setNotesModal({ evaluatorId: u.id, evaluatorName: u.name, isExternal: u.isExternal }),
                  },
                  contestStatus === 'excluido'
                    ? { label: 'Habilitar', handler: () => handleInclude(u) }
                    : { label: 'Excluir del concurso', handler: () => handleExclude(u) },
                ];
                return (
                <tr key={u.id} className={!u.isActive ? styles.rowInactive : undefined}>
                  <td className={styles.nameCell}>
                    <div className={styles.applicantName}>{u.name}</div>
                    <div className={styles.applicantEmail}>{u.email}</div>
                  </td>
                  <td>
                    <span className={`uc-status ${u.isExternal ? styles.badgeExterno : styles.badgeInterno}`}>
                      {u.isExternal ? 'Externo' : 'Interno'}
                    </span>
                  </td>
                  <td>
                    {contestStatus === 'asignado' && <span className={`uc-status ${styles.badgeAsignado}`}>Asignado</span>}
                    {contestStatus === 'habilitado' && <span className={`uc-status ${styles.badgeHabilitado}`}>Habilitado</span>}
                    {contestStatus === 'excluido' && <span className={`uc-status ${styles.badgeExcluido}`}>Excluido</span>}
                  </td>
                  <td className={styles.scoreCell}>
                    {profile?.score != null ? (
                      <span className={styles.scoreValue}>
                        <i className="uc-icon">star</i>
                        {profile.score.toFixed(1)}
                      </span>
                    ) : <span className={styles.muted}>—</span>}
                  </td>
                  <td className={styles.noteCell}>
                    {profile?.latestNote
                      ? <span title={profile.latestNote}>{profile.latestNote}</span>
                      : <span className={styles.muted}>—</span>}
                  </td>
                  <td className={styles.actionCell}>
                    <EvaluadorActionsMenu actions={actions} />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Notes / score modal */}
      {notesModal && (
        <EvaluatorNotesModal
          isOpen={!!notesModal}
          evaluatorId={notesModal.evaluatorId}
          evaluatorName={notesModal.evaluatorName}
          isExternal={notesModal.isExternal}
          onClose={() => setNotesModal(null)}
          onProfileChanged={() => handleProfileChanged(notesModal.evaluatorId)}
        />
      )}

      {/* Add modal */}
      {showAdd && (
        <div className={styles.overlay} onClick={closeAdd}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className="uc-h4">Añadir evaluador</h2>
              <Button variant="secondary" size="sm" onClick={closeAdd}>
                ×
              </Button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Nombre completo</label>
                <input
                  className={styles.input}
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Ej: María Pérez"
                  autoFocus
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Email</label>
                <input
                  className={styles.input}
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  placeholder="Ej: mperez@uc.cl"
                />
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Tipo</label>
                <div className={styles.radioGroup}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="tipo"
                      checked={!addIsExternal}
                      onChange={() => setAddIsExternal(false)}
                    />
                    Interno (UC)
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="tipo"
                      checked={addIsExternal}
                      onChange={() => setAddIsExternal(true)}
                    />
                    Externo
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <Button variant="secondary" onClick={closeAdd}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleAdd}
                disabled={!addName.trim() || !addEmail.trim() || addSaving}
              >
                {addSaving ? 'Guardando...' : 'Crear evaluador'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvaluadoresTab;
